import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { PWA_CHECK_EVENT } from "./pwa";

export default function SessionAccountMenu({ children }: { children: ReactNode }) {
  const { t } = useTranslation("auth");
  const { t: updateText } = useTranslation("pwaInstall");
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  return (
    <div
      className="session-account-menu"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        trigger.current?.focus();
      }}
    >
      <button
        type="button"
        className="session-account-trigger"
        data-session-account-trigger
        ref={trigger}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <span>{t("accountSupport")}</span>
        <svg aria-hidden="true" viewBox="0 0 20 20" width="16" height="16" fill="none">
          <path d={open ? "m5 7 5 5 5-5" : "m5 12 5-5 5 5"} stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>
      <div id={id} className="session-account-panel" hidden={!open}>
        {children}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            window.dispatchEvent(new CustomEvent(PWA_CHECK_EVENT));
          }}
        >
          {updateText("checkUpdate")}
        </button>
      </div>
    </div>
  );
}
